import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, Radio } from '@mui/material';
import clsx from 'clsx';
import React from 'react';

import classes from './RadioCard.module.scss';

const RADIO_CARD_MODES = {
    RADIO: 'radio',
    CHECKBOX: 'checkbox'
} as const;

type RadioCardMode = (typeof RADIO_CARD_MODES)[keyof typeof RADIO_CARD_MODES];

interface RadioCardProps {
    title: string;
    checked?: boolean;
    description?: string;
    content?: string;
    mode?: RadioCardMode;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const RadioCard: React.FC<RadioCardProps> = ({
    mode = RADIO_CARD_MODES.RADIO,
    ...props
}) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    const [isOpen, setIsOpen] = React.useState(false);

    const toggleValue = () => {
        inputRef.current?.click();
    };

    return (
        <div className={classes.root}>
            <div className={classes.input}>
                {mode === RADIO_CARD_MODES.CHECKBOX ? (
                    <Checkbox
                        disableRipple
                        checked={props.checked}
                        onChange={props.onChange}
                        inputRef={inputRef}
                    />
                ) : (
                    <Radio
                        disableRipple
                        inputRef={inputRef}
                        checked={props.checked}
                        checkedIcon={
                            <FontAwesomeIcon icon={faCheckCircle} className={classes.checkIcon} />
                        }
                        onChange={props.onChange}
                    />
                )}
            </div>

            <div
                className={clsx(classes.info, {
                    [classes.infoExpanded]: isOpen
                })}
            >
                <div className={classes.header}>
                    <p className={classes.title} onClick={toggleValue}>
                        {props.title}
                    </p>

                    {props.description && (
                        <p className={classes.description}>{props.description}</p>
                    )}
                </div>

                {props.content && isOpen && <p className={classes.content}>{props.content}</p>}

                {props.content && (
                    <div className={classes.moreInfo}>
                        <button
                            type="button"
                            className={classes.toggle}
                            aria-label={props.content ? 'Hide info' : 'More info'}
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            {isOpen ? 'Hide info' : 'More info'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
